import { Dropdown } from "./Dropdown";
import type { BadgeVariant } from "../Display/Badge";

interface EnumDropdownOptions<T extends string> {
  /**
   * Renders a subtle color dot beside each option (and the selected label) so
   * users can recognise categories at a glance. Pair it with the existing
   * `xxxVariant()` helpers in `documentEnums` etc.
   */
  colorForItem?: ((value: T) => BadgeVariant | undefined) | undefined;
  /**
   * Lists the options alphabetically by label instead of in the order the enum
   * declares them. Off by default, because most enums here are already in a
   * meaningful order (pipeline stages, claim states) that sorting would destroy.
   * Turn it on where the declaration order carries no meaning of its own — a
   * filter fed by a database enum, say — and a reader is hunting for one entry.
   */
  sorted?: boolean | undefined;
}

/**
 * Factory that creates a type-safe single-select dropdown from a TypeScript enum.
 * Always optional — placeholder shown when value is undefined, selecting clears to undefined.
 *
 * Usage:
 * ```ts
 * const MyDropdown = createEnumDropdown(MyEnum, describeMyEnum, {
 *   colorForItem: myEnumVariant,
 * });
 * <MyDropdown value={value} onChange={setValue} placeholder="Select..." />
 * ```
 */
export function createEnumDropdown<T extends string>(
  enumObject: Record<string, T>,
  describer: (value: T) => string,
  { colorForItem, sorted = false }: EnumDropdownOptions<T> = {},
) {
  const values = Object.values(enumObject);
  const options = values.map((v) => ({
    label: describer(v),
    value: v,
  }));

  if (sorted) {
    options.sort((a, b) => a.label.localeCompare(b.label));
  }

  const dropdownColorForItem = colorForItem
    ? (value: string) => colorForItem(value as T)
    : undefined;

  function EnumDropdownComponent({
    value,
    onChange,
    placeholder = "No filter",
    "data-testid": dataTestid,
  }: {
    value: T | undefined;
    onChange: (value: T | undefined) => void;
    placeholder?: string | undefined;
    "data-testid"?: string | undefined;
  }) {
    return (
      <Dropdown
        options={options}
        value={value}
        onChange={(v) => onChange(v as T)}
        onClear={() => onChange(undefined)}
        placeholder={placeholder}
        colorForItem={dropdownColorForItem}
        data-testid={dataTestid}
      />
    );
  }

  return EnumDropdownComponent;
}
